import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser } = useContext(AuthContext);
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/membership');
      return;
    }

    if (!user) {
      navigate('/auth');
      return;
    }

    const pollPaymentStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/payments/status/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          
          const userResponse = await axios.get(`${API}/api/auth/me`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            withCredentials: true
          });
          setUser(userResponse.data);
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    pollPaymentStatus();
  }, [searchParams, navigate, user, attempts, setUser]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mb-6"></div>
          <h2 className="text-2xl font-black text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>Processing payment...</h2>
          <p className="text-slate-600">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center">
          <div className="inline-flex items-center justify-center w-32 h-32 bg-lime-400 rounded-full mb-8 border-4 border-black shadow-brutal-lg animate-slide-in">
            <Check className="w-16 h-16 text-slate-900" strokeWidth={4} />
          </div>
          
          <div className="bg-white border-4 border-black rounded-3xl p-10 shadow-brutal-lg mb-8">
            <h1 className="text-5xl font-black text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>
              You're in.
            </h1>
            <p className="text-xl text-slate-600 font-medium mb-8">
              Your payment was successful. Full member access is now active.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-indigo-100 border-2 border-black rounded-2xl p-6">
                <Sparkles className="w-12 h-12 text-indigo-500 mx-auto mb-3" />
                <h3 className="font-bold text-slate-900 mb-1">Member Connections</h3>
                <p className="text-sm text-slate-600">Connect with anyone</p>
              </div>
              <div className="bg-pink-100 border-2 border-black rounded-2xl p-6">
                <Sparkles className="w-12 h-12 text-pink-500 mx-auto mb-3" />
                <h3 className="font-bold text-slate-900 mb-1">Messaging</h3>
                <p className="text-sm text-slate-600">Chat with members</p>
              </div>
              <div className="bg-lime-100 border-2 border-black rounded-2xl p-6">
                <Sparkles className="w-12 h-12 text-lime-600 mx-auto mb-3" />
                <h3 className="font-bold text-slate-900 mb-1">Member Badge</h3>
                <p className="text-sm text-slate-600">Shown on your profile</p>
              </div>
            </div>

            <Button
              data-testid="go-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              className="w-full md:w-auto bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold text-lg px-12 h-14 shadow-brutal"
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white border-4 border-black rounded-3xl p-10 shadow-brutal-lg">
          <h2 className="text-3xl font-black text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>Payment Issue</h2>
          <p className="text-slate-600 mb-8">
            {status === 'expired' && 'Your payment session has expired.'}
            {status === 'timeout' && 'Payment verification is taking longer than expected.'}
            {status === 'error' && 'There was an error processing your payment.'}
          </p>
          <Button
            onClick={() => navigate('/membership')}
            className="w-full bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
          >
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
}